import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useState } from 'react';
import { Autocomplete as MuiAutocomplete } from '@material-ui/lab';
import { compact } from 'lodash-es';
import { VariableSizeList } from 'react-window';
import { ArrowSolidDown } from '../../icons';
import { useIsInBottomSheet } from '../../providers';
import { useDevice } from '../../providers/Device';
import { useBiAction } from '../BiTracking';
import { BottomSheet } from '../BottomSheet';
import { CloseButton } from '../CloseButton/CloseButton';
import { Spinner } from '../Spinner';
import { TextField } from '../TextField';
import { useAutocompleteStyles } from './Autocomplete.styles';
import { buildAutocompleteBottomSheet, buildErrorInfoSuffix, deriveAutocompleteDisplayValue, shouldShowHelperText } from './Autocomplete.utils';
import { MobileAutocompleteCard } from './MobileAutocompleteCard';
import { useAsyncOptions } from './useAsyncOptions';
// WHY: popup options virtualization implemented based on https://v4.mui.com/components/autocomplete/#virtualization
const LISTBOX_PADDING_PX = 8;
function renderRow(props) {
    const { data, index, style } = props;
    return /*#__PURE__*/ React.cloneElement(data[index], {
        style: _object_spread_props(_object_spread({}, style), {
            top: style.top + LISTBOX_PADDING_PX
        })
    });
}
const OuterElementContext = /*#__PURE__*/ React.createContext({});
const OuterElementType = /*#__PURE__*/ React.forwardRef((props, ref)=>{
    const outerProps = React.useContext(OuterElementContext);
    return /*#__PURE__*/ _jsx("div", _object_spread({
        ref: ref
    }, props, outerProps));
});
function useResetCache(data) {
    const ref = React.useRef(null);
    React.useEffect(()=>{
        if (ref.current != null) {
            ref.current.resetAfterIndex(0, true);
        }
    }, [
        data
    ]);
    return ref;
}
// Adapter for react-window
const ListboxComponent = /*#__PURE__*/ React.forwardRef(function ListboxComponent(props, ref) {
    const { children } = props, other = _object_without_properties(props, [
        "children"
    ]);
    const itemData = React.Children.toArray(children);
    const itemCount = itemData.length;
    const itemSize = 48;
    const getChildSize = (child)=>{
        var _child_props;
        const isTwoLiner = compact(child === null || child === void 0 ? void 0 : (_child_props = child.props) === null || _child_props === void 0 ? void 0 : _child_props.children.props.children).length === 2;
        return isTwoLiner ? 48 : 36;
    };
    const getHeight = ()=>{
        if (itemCount > 8) {
            return 8 * itemSize;
        }
        return itemData.map(getChildSize).reduce((a, b)=>a + b, 0);
    };
    const gridRef = useResetCache(itemCount);
    return /*#__PURE__*/ _jsx("div", {
        ref: ref,
        children: /*#__PURE__*/ _jsx(OuterElementContext.Provider, {
            value: other,
            children: /*#__PURE__*/ _jsx(VariableSizeList, {
                itemData: itemData,
                height: getHeight() + 2 * LISTBOX_PADDING_PX,
                width: "100%",
                ref: gridRef,
                outerElementType: OuterElementType,
                innerElementType: "ul",
                itemSize: (index)=>getChildSize(itemData[index]),
                overscanCount: 5,
                itemCount: itemCount,
                children: renderRow
            })
        })
    });
});
function MobileAutocomplete(_0) {
    let { className, textFieldProps, loading, disableEndAdornment, noOptionsText, error, helperText, errorInfo, collapseHelperText = true, popoverFooter, searchBiName, fullHeightSheet = true } = _0, props = _object_without_properties(_0, [
        "className",
        "textFieldProps",
        "loading",
        "disableEndAdornment",
        "noOptionsText",
        "error",
        "helperText",
        "errorInfo",
        "collapseHelperText",
        "popoverFooter",
        "searchBiName",
        "fullHeightSheet"
    ]);
    const errorInfoSuffix = buildErrorInfoSuffix({
        error,
        errorInfo,
        testId: props.testId
    });
    // eslint-disable-next-line no-restricted-syntax -- WHY: BottomSheet IS the declarative overlay the rule points at, and it takes `open` as a prop, so this seagull surface owns that state itself instead of deferring to showModal.
    const [isOpen, setIsOpen] = useState(false);
    const displayValue = deriveAutocompleteDisplayValue(props);
    const showClearButton = !!displayValue && !props.disableClearable;
    const handleClear = (e)=>{
        var _props_onChange;
        e.stopPropagation();
        const clearValue = props.multiple ? [] : null;
        (_props_onChange = props.onChange) === null || _props_onChange === void 0 ? void 0 : _props_onChange.call(props, {}, clearValue, 'clear', undefined);
    };
    const openBottomSheet = ()=>setIsOpen(true);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: className,
                "data-testid": props.testId,
                role: "button",
                tabIndex: 0,
                onClick: openBottomSheet,
                onKeyDown: (e)=>{
                    if (e.key === 'Enter' || e.key === ' ') {
                        openBottomSheet();
                    }
                },
                children: /*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                    collapseHelperText: collapseHelperText,
                    error: !!error,
                    helperText: shouldShowHelperText({
                        error,
                        errorInfo
                    }) ? helperText : undefined
                }, _object_spread_props(_object_spread({}, textFieldProps), {
                    noBi: true
                })), {
                    inputProps: {
                        readOnly: true,
                        value: displayValue
                    },
                    testId: props.testId && "".concat(props.testId, "-textfield"),
                    suffix: !disableEndAdornment && /*#__PURE__*/ _jsxs("span", {
                        style: {
                            display: 'flex',
                            alignItems: 'center'
                        },
                        children: [
                            errorInfoSuffix,
                            loading ? /*#__PURE__*/ _jsx(Spinner, {
                                testId: "".concat(props.testId, "-spinner"),
                                size: 32
                            }) : null,
                            showClearButton && /*#__PURE__*/ _jsx(CloseButton, {
                                // eslint-disable-next-line custom/no-hardcoded-bi-name-in-seagull -- WHY: the mobile field's clear action is an inherent fixed-semantic clear; consumers cannot rename it (rule 6a), and the parent's select-only handleChange does not log the 'clear' reason, so this is not a double-fire.
                                bi: {
                                    name: 'clear'
                                },
                                size: "small",
                                noPadding: true,
                                testId: "".concat(props.testId, "-mobile-clear"),
                                onClick: handleClear
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                "data-testid": "".concat(props.testId, "-popup-icon"),
                                children: /*#__PURE__*/ _jsx(ArrowSolidDown, {})
                            })
                        ]
                    })
                }))
            }),
            /*#__PURE__*/ _jsx(BottomSheet, {
                open: isOpen,
                onClose: ()=>setIsOpen(false),
                fullHeight: fullHeightSheet,
                children: buildAutocompleteBottomSheet(_object_spread_props(_object_spread({}, props), {
                    loading,
                    noOptionsText,
                    label: textFieldProps === null || textFieldProps === void 0 ? void 0 : textFieldProps.label,
                    popoverFooter,
                    searchBiName
                }))
            })
        ]
    });
}
export function Autocomplete(_0) {
    let { className, textFieldProps, loading, disableEndAdornment, virtualizeOptions, lazyOptions, disableSelectedHighlight, noOptionsText, disableTyping, menuWidth, listboxMaxHeight, error, helperText, errorInfo, collapseHelperText = true, popoverFooter, dividers, mobile, fullHeightSheet, bi, noBi: _noBi, testId } = _0, props = _object_without_properties(_0, [
        "className",
        "textFieldProps",
        "loading",
        "disableEndAdornment",
        "virtualizeOptions",
        "lazyOptions",
        "disableSelectedHighlight",
        "noOptionsText",
        "disableTyping",
        "menuWidth",
        "listboxMaxHeight",
        "error",
        "helperText",
        "errorInfo",
        "collapseHelperText",
        "popoverFooter",
        "dividers",
        "mobile",
        "fullHeightSheet",
        "bi",
        "noBi",
        "testId"
    ]);
    const { isMobileDevice } = useDevice();
    const isInBottomSheet = useIsInBottomSheet();
    const logBiAction = useBiAction();
    const handleChange = useCallback(function(event, value, reason) {
        for(var _len = arguments.length, rest = new Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++){
            rest[_key - 3] = arguments[_key];
        }
        var _props_onChange;
        if ((bi === null || bi === void 0 ? void 0 : bi.name) && (reason === 'select-option' || reason === 'create-option')) {
            logBiAction({
                entityType: 'autocomplete',
                entityName: bi.name,
                actionType: 'select',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : ''
                })
            });
        }
        (_props_onChange = props.onChange) === null || _props_onChange === void 0 ? void 0 : _props_onChange.call(props, event, value, reason, ...rest);
    }, [
        bi,
        logBiAction,
        props.onChange,
        testId
    ]);
    const hasMultipleChips = !!props.multiple && Array.isArray(props.value) && props.value.length > 0;
    const classes = useAutocompleteStyles({
        hasMultipleChips,
        disableSelectedHighlight
    });
    const [isFocused, setIsFocused] = useState(false);
    const isAsync = props.loadOptions != null;
    const { options: asyncOptions, loading: asyncLoading, search, cancel: cancelSearch } = useAsyncOptions(props.loadOptions, props.loadOptionsDebounceMs);
    const handleInputChange = (event, value, reason)=>{
        var _props_onInputChange;
        if (isAsync) {
            search(reason === 'clear' ? '' : value);
        }
        (_props_onInputChange = props.onInputChange) === null || _props_onInputChange === void 0 ? void 0 : _props_onInputChange.call(props, event, value, reason);
    };
    const DividerListbox = dividers ? /*#__PURE__*/ React.forwardRef((listboxProps, ref)=>{
        const { children } = listboxProps, rest = _object_without_properties(listboxProps, [
            "children"
        ]);
        const items = React.Children.toArray(children);
        const resolvedIndices = new Set(dividers.map((d)=>d < 0 ? items.length + d : d));
        const result = [];
        items.forEach((child, i)=>{
            if (resolvedIndices.has(i)) {
                result.push(/*#__PURE__*/ _jsx("div", {
                    "data-testid": testId ? "".concat(testId, "-option-divider") : undefined,
                    className: classes.optionDivider
                }, "divider-".concat(i)));
            }
            result.push(child);
        });
        return /*#__PURE__*/ _jsx("ul", _object_spread_props(_object_spread({
            ref: ref
        }, rest), {
            children: result
        }));
    }) : undefined;
    if (virtualizeOptions) {
        props.ListboxComponent = ListboxComponent;
    }
    const shouldResolveLazyOptions = virtualizeOptions && isFocused || isInBottomSheet || isMobileDevice;
    if (lazyOptions != null && shouldResolveLazyOptions) {
        props.options = lazyOptions();
    }
    const searchBiName = (bi === null || bi === void 0 ? void 0 : bi.name) ? "".concat(bi.name, "_search") : undefined;
    if (isInBottomSheet || !(mobile === null || mobile === void 0 ? void 0 : mobile.renderAsCard) && isMobileDevice) {
        return /*#__PURE__*/ _jsx(MobileAutocomplete, _object_spread_props(_object_spread({
            className: className,
            testId: testId,
            textFieldProps: textFieldProps,
            loading: loading,
            disableEndAdornment: disableEndAdornment,
            noOptionsText: noOptionsText,
            error: error,
            helperText: helperText,
            errorInfo: errorInfo,
            collapseHelperText: collapseHelperText,
            popoverFooter: popoverFooter,
            searchBiName: searchBiName,
            fullHeightSheet: fullHeightSheet
        }, props), {
            onChange: handleChange
        }));
    } else if ((mobile === null || mobile === void 0 ? void 0 : mobile.renderAsCard) && isMobileDevice) {
        return /*#__PURE__*/ _jsx(MobileAutocompleteCard, _object_spread_props(_object_spread({
            testId: testId,
            textFieldProps: textFieldProps,
            loading: loading,
            noOptionsText: noOptionsText,
            error: error,
            helperText: helperText,
            errorInfo: errorInfo,
            popoverFooter: popoverFooter,
            searchBiName: searchBiName,
            fullHeightSheet: fullHeightSheet,
            extraCardItems: mobile.extraCardItems,
            hideRemoveAction: mobile.hideRemoveAction
        }, props), {
            onChange: handleChange
        }));
    }
    return /*#__PURE__*/ _jsx(MuiAutocomplete, _object_spread_props(_object_spread({
        className: className,
        "data-testid": testId,
        classes: {
            option: classes.option,
            endAdornment: classes.endAdornment,
            popper: classes.popper,
            paper: classes.paper
        },
        renderInput: (params)=>/*#__PURE__*/ _jsx(TextField, _object_spread_props(_object_spread({
                collapseHelperText: collapseHelperText,
                inputRootClass: classes.inputRoot,
                error: !!error,
                helperText: shouldShowHelperText({
                    error,
                    errorInfo
                }) ? helperText : undefined
            }, _object_spread_props(_object_spread({}, textFieldProps, params), {
                noBi: true
            })), {
                inputProps: _object_spread_props(_object_spread({}, params.inputProps), {
                    readOnly: disableTyping || false
                }),
                testId: testId && "".concat(testId, "-textfield"),
                suffix: !disableEndAdornment && /*#__PURE__*/ _jsxs("span", {
                    style: {
                        marginBlockStart: 2,
                        marginInlineEnd: 24
                    },
                    children: [
                        buildErrorInfoSuffix({
                            error,
                            errorInfo,
                            testId
                        }),
                        loading ? /*#__PURE__*/ _jsx(Spinner, {
                            testId: "".concat(testId, "-spinner"),
                            size: 32
                        }) : null,
                        params.InputProps.endAdornment
                    ]
                })
            })),
        popupIcon: /*#__PURE__*/ _jsx("div", {
            "data-testid": "".concat(testId, "-popup-icon"),
            children: /*#__PURE__*/ _jsx(ArrowSolidDown, {})
        }),
        ListboxProps: _object_spread({
            'data-testid': "".concat(testId, "-listbox")
        }, listboxMaxHeight != null ? {
            style: {
                maxHeight: listboxMaxHeight
            }
        } : {}),
        noOptionsText: /*#__PURE__*/ _jsx("div", {
            "data-testid": "".concat(testId, "-no-options-text"),
            children: noOptionsText
        })
    }, props, isAsync ? {
        options: asyncOptions,
        onInputChange: handleInputChange
    } : {}), {
        loading: isAsync ? loading || asyncLoading : loading,
        ListboxComponent: DividerListbox !== null && DividerListbox !== void 0 ? DividerListbox : undefined,
        onChange: handleChange,
        PaperComponent: (paperProps)=>React.Children.toArray(paperProps.children).length === 0 && !popoverFooter ? null : /*#__PURE__*/ _jsxs("div", _object_spread_props(_object_spread({}, paperProps), {
                style: {
                    width: menuWidth
                },
                children: [
                    paperProps.children,
                    popoverFooter && popoverFooter
                ]
            })),
        onFocus: (ev)=>{
            var _props_onFocus;
            setIsFocused(true);
            (_props_onFocus = props.onFocus) === null || _props_onFocus === void 0 ? void 0 : _props_onFocus.call(props, ev);
        },
        onBlur: (ev)=>{
            var _props_onBlur;
            setIsFocused(false);
            if (isAsync) {
                cancelSearch();
            }
            (_props_onBlur = props.onBlur) === null || _props_onBlur === void 0 ? void 0 : _props_onBlur.call(props, ev);
        }
    }));
}
